/**
 * O valor de um campo personalizado na tela (#568): como se mostra, como se
 * escreve e o que falta preencher.
 *
 * O servidor confere tudo de novo. Aqui é para a pessoa ver o valor do jeito
 * que fala ("Sim", "R$ 1.500", "19/10/2026") e para o formulário não deixar
 * salvar o obrigatório vazio.
 */

import type {
  CampoPersonalizado,
  MudancaDeCampos,
  ValorDeCampo,
  ValoresDeCampos,
} from '@cliqcrm/contracts';
import { dinheiro } from '@/shared/lib/formato';

/** O valor como texto de leitura, ou `null` quando vazio. */
export function textoDoValor(
  campo: CampoPersonalizado,
  valor: ValorDeCampo | undefined,
): string | null {
  if (valor === undefined) return null;
  const nomeDaOpcao = (id: string) => campo.opcoes.find((opcao) => opcao.id === id)?.nome ?? null;
  switch (campo.tipo) {
    case 'sim_nao':
      return valor === true ? 'Sim' : valor === false ? 'Não' : null;
    case 'dinheiro':
      return typeof valor === 'object' && !Array.isArray(valor) ? dinheiro(valor) : null;
    case 'data': {
      if (typeof valor !== 'string') return null;
      const [ano, mes, dia] = valor.split('-');
      return ano && mes && dia ? `${dia}/${mes}/${ano}` : valor;
    }
    case 'numero':
      return typeof valor === 'number' ? valor.toLocaleString('pt-BR') : null;
    case 'lista_unica':
      return typeof valor === 'string' ? nomeDaOpcao(valor) : null;
    case 'lista_multipla':
      return Array.isArray(valor)
        ? valor
            .map(nomeDaOpcao)
            .filter((nome): nome is string => nome !== null)
            .join(', ') || null
        : null;
    default:
      return typeof valor === 'string' ? valor : null;
  }
}

/** Os campos que o formulário mostra: os vivos, na ordem do catálogo. */
export function camposVivos(campos: readonly CampoPersonalizado[]): CampoPersonalizado[] {
  return campos.filter((campo) => !campo.arquivado).sort((a, b) => a.ordem - b.ordem);
}

/** O valor depois da mudança que a pessoa fez no formulário. */
export function valorAtual(
  id: string,
  valores: ValoresDeCampos,
  mudanca: MudancaDeCampos,
): ValorDeCampo | undefined {
  if (id in mudanca) return mudanca[id] ?? undefined;
  return valores[id];
}

/**
 * Os obrigatórios vazios, para o formulário não deixar salvar. Só os que valem
 * desde o começo: o "a partir da etapa" quem confere é o servidor, ao mover.
 */
export function obrigatoriosVazios(
  campos: readonly CampoPersonalizado[],
  valores: ValoresDeCampos,
  mudanca: MudancaDeCampos,
): CampoPersonalizado[] {
  return camposVivos(campos).filter(
    (campo) =>
      campo.obrigatorio &&
      campo.obrigatorio_desde_etapa === null &&
      valorAtual(campo.id, valores, mudanca) === undefined,
  );
}

/** Os ids de campo que a recusa do servidor apontou (`campos.<id>` em `erros`). */
export function camposDaRecusa(erros: readonly { campo: string }[] | undefined): string[] {
  return (erros ?? [])
    .map((erro) => /^campos\.(.+)$/.exec(erro.campo)?.[1])
    .filter((id): id is string => id !== undefined);
}
